import service from './http'
import serve from "@/utils/serve";
import mountQuery from "@/utils/mountQuery";
import WorkspaceSummary, {WorkspaceOverview} from "@/types/WorkspaceSummary";
import {ref} from "vue";
import {AccessLevel} from "@/types/AccessLevel";
import {WorkspaceRole} from "@/types/Workspace";

/** Um ambiente na lista de quem está entrando: o que o menu da barra mostra de cada um. */
export type WorkspaceOption = { alias: string, title: string, closed?: boolean, accessLevel?: AccessLevel }

/**
 * A lista de ambientes de quem está entrando, reativa — o menu da barra a mostra.
 *
 * Mora aqui, e não no menu, porque quem a muda passa por este serviço: criar, alterar, encerrar e
 * excluir um ambiente buscam a lista de novo, e o menu se atualiza na hora (pedido do usuário) —
 * guardada no menu, ela só mudava na próxima vez que ele fosse aberto. E fica no módulo porque o
 * menu não sobrevive à troca de ambiente (o `reloadSystem` do App o remonta).
 */
export const workspaceOptions = ref<WorkspaceOption[] | null>(null)

/** A busca em curso: quem pede enquanto ela corre espera a mesma, e não dispara outra. */
let inFlight: Promise<WorkspaceOption[]> | null = null

class SystemService {
    async add(system: any) {
        const created = await serve(async () => await service.post('systems', system))
        this.refreshInBackground()
        return created
    }

    async get({systemAlias}: { systemAlias: string }, options?: {ideas?: boolean, workflows?: boolean, domains?: boolean}) {
        const queryOptions: string[] = []
        if(options?.ideas) queryOptions.push("ideas")
        if(options?.workflows) queryOptions.push("workflows")
        if(options?.domains) queryOptions.push("domains")
        return await serve(async () => await service.get(`systems/${systemAlias}${mountQuery({options: queryOptions})}`))
    }

    /** Alterar o nome e encerrar ou reabrir (`setClosed`, que passa por aqui) mudam a lista. */
    async update({systemAlias}: { systemAlias: string }, system: any) {
        const updated = await serve(async () => await service.patch(`systems/${systemAlias}`, system))
        this.refreshInBackground()
        return updated
    }

    /**
     * Os papéis do ambiente, a lista inteira. Rota própria, e não um campo do `update`: o portão
     * pede designer para `/roles`, e administrador para o ambiente em si.
     */
    async saveRoles({systemAlias}: { systemAlias: string }, roles: WorkspaceRole[]) {
        await serve(async () => await service.put(`systems/${systemAlias}/roles`, roles))
    }

    /**
     * Encerra ou reabre o ambiente.
     *
     * Existe como método próprio, e não como um `update` qualquer, porque **o corpo da
     * reabertura é parte do contrato com o servidor**: lá, num ambiente encerrado, a única
     * escrita que passa é um PATCH cujo corpo é exatamente `{closed: false}` — qualquer campo
     * a mais e já não é reabrir, é reabrir E alterar, que é o que estava proibido.
     *
     * Montado em dois lugares — o design do ambiente e o menu —, um deles acabaria mandando o
     * título junto e levaria um 403 sem motivo aparente.
     */
    async setClosed({systemAlias}: { systemAlias: string }, closed: boolean) {
        return await this.update({systemAlias}, {closed})
    }

    async list() {
        return await serve(async () => await service.get('systems'))
    }

    /** O que a página de entrada do ambiente mostra — ver `WorkspaceSummary`. */
    async summary({systemAlias}: { systemAlias: string }) {
        return await serve(async () => await service.get(`systems/${systemAlias}/summary`)) as WorkspaceSummary
    }

    /** Os números e os alterados de todos os ambientes de quem pede, para a tela inicial. */
    async overview() {
        return await serve(async () => await service.get('overview')) as WorkspaceOverview[]
    }

    async delete({systemAlias}: { systemAlias: string }) {
        const deleted = await serve(async () => await service.delete(`systems/${systemAlias}`))
        this.refreshInBackground()
        return deleted
    }

    /** Busca a lista de ambientes de novo, para `workspaceOptions` — uma busca por vez. */
    refreshOptions(): Promise<WorkspaceOption[]> {
        if (!inFlight) inFlight = (this.list() as Promise<WorkspaceOption[]>)
            .then(options => (workspaceOptions.value = options))
            .finally(() => { inFlight = null })
        return inFlight
    }

    /**
     * Depois de uma alteração: a gravação já deu certo, e uma falha ao rebuscar a lista não pode
     * virar falha dela — o menu fica com a lista de antes até a próxima busca.
     */
    private refreshInBackground() {
        this.refreshOptions().catch(() => undefined)
    }
}

export default SystemService